import { tryParseJSON } from "@/components/document-chat/utils";
import type { KarlCustomPayload } from "@/components/document-chat/services/streamEventTypes";
import { KarlCustomEvent } from "@/constants/enums";
// Types are globally available from @/types/chat-sse.d.ts
import type { DocumentChatRuntime } from "./types";
import {
  handleToolCallEvent,
  handleReasoningEvent,
  handleDataEvent,
  handleFetchContextEvent,
  handleProgressEvent,
  handleMessagesEvent,
  handleErrorEvent
} from "./handlers";
import { extractAndSetSources } from "./utils";
import type { TFunction } from "i18next";

/**
 * Gets a snapshot of the current runtime state
 */
type GetStateSnapshot = () => {
  currentNode: string;
  processingStagesComplete: boolean;
};

/**
 * Processes all SSE events from the stream
 *
 * @param events - Array of parsed SSE blocks
 * @param session - The current stream session
 * @param runtime - The document chat runtime store interface
 * @param cancel - Function to cancel the stream
 * @param getStateSnapshot - Function to get current state snapshot
 * @param t - Translation function
 */
export function processSSEEvents(
  events: ParsedSSEBlock[],
  session: StreamSession,
  runtime: DocumentChatRuntime,
  cancel: () => void,
  getStateSnapshot: GetStateSnapshot,
  t: TFunction
): void {
  for (const evt of events) {
    // Handle error events from the backend
    if (evt.eventName === "error") {
      handleErrorEvent(evt.dataStr, session, runtime, cancel, t);
      continue;
    }

    const payload = tryParseJSON<KarlCustomPayload>(evt.dataStr);
    if (!payload) {
      continue;
    }

    // Handle custom control flow events
    if (!evt.eventName || evt.eventName === "custom") {
      handleCustomEvent(payload, session, runtime, cancel);
      continue; // done with custom event
    }

    // Handle token streams over the messages channel
    if (evt.eventName === "messages") {
      // Read fresh state from store (not stale selector values)
      const state = getStateSnapshot();
      handleMessagesEvent(payload, session, runtime, state.currentNode, state.processingStagesComplete);
    }

    /* Handle other event types here */
  }
}

/**
 * Processes custom control flow events based on their type
 * Main routing logic for the SSE stream events
 *
 * @param payload - The custom event payload
 * @param session - The current stream session
 * @param runtime - The document chat runtime store interface
 * @param cancel - Function to cancel the stream
 */
function handleCustomEvent(
  payload: KarlCustomPayload,
  session: StreamSession,
  runtime: DocumentChatRuntime,
  cancel: () => void
): void {
  const type = String(payload.type ?? "");
  if (type === KarlCustomEvent.TOOL_CALL) {
    handleToolCallEvent(payload, session, runtime);
    return;
  }
  if (type === KarlCustomEvent.REASONING) {
    handleReasoningEvent(payload, session, runtime);
    return;
  }
  if (type === KarlCustomEvent.CHUNK) {
    const token = typeof payload.content === "string" ? payload.content : "";
    if (token && session.thinkingActive) runtime.appendThinking(token);
    return;
  }
  if (type === KarlCustomEvent.DATA) {
    handleDataEvent(payload, session, runtime, cancel, (contentObj) =>
      extractAndSetSources(contentObj, runtime)
    );
    return;
  }
  if (type === KarlCustomEvent.PROGRESS) {
    handleProgressEvent(payload, runtime);
    return;
  }
  if (type === KarlCustomEvent.FETCH_CONTEXT) {
    handleFetchContextEvent(payload, runtime);
  }
  // Unknown custom type: ignore silently
}
